import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {HTMLElement | null} */
  #returnFocusTo = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('dialog:close', this.#restoreLauncherFocus);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('dialog:close', this.#restoreLauncherFocus);
  }

  #handleCartAdd = (event) => {
    const data = event.detail?.data;

    // CartAddEvent shares the cart:update event name. Only primary product-form
    // additions should open the drawer; quantity, discount, note and in-cart
    // recommendation updates must remain in place.
    if (data?.didError || data?.source !== 'product-form-component') return;

    if (this.hasAttribute('auto-open')) {
      this.#rememberLauncher();
      this.showDialog();
      this.#trackOpen('add_to_cart');
    }
  };

  open() {
    this.#rememberLauncher();
    this.showDialog();
    this.#trackOpen('cart_icon');

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /** @param {string} source */
  #trackOpen(source) {
    const detail = {
      event: 'pp_cart_open',
      cart_device: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
      source,
    };

    window.dispatchEvent(new CustomEvent('cart:v2:analytics', { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
  }

  #rememberLauncher() {
    this.#returnFocusTo = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : this.querySelector('.header-actions__action');
  }

  #restoreLauncherFocus = () => {
    const fallback = this.querySelector('.header-actions__action');
    const target = this.#returnFocusTo?.isConnected ? this.#returnFocusTo : fallback;
    queueMicrotask(() => target?.focus({ preventScroll: true }));
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
